import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Settings } from '@xeetr/types/models'
import { useContext, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'

import { ActiveTenantContext } from '../context/common/Context'
import { invalidateTenantQueries } from '../lib/mutationHelpers'
import { queryKeys } from '../lib/queryKeys'
import { SettingsService } from '../services/Settings.service'

import { useTenantOptionalService } from './common/useServiceFactory'

import { showErrorToastUnlessPermissionDenied, showToast } from '@/utils/toastMessage'

interface UseSettingsReturn {
  settings: Settings | undefined
  isLoading: boolean
  isError: boolean
  error: Error | null
  isSaving: boolean
  save: (settings: Settings) => void
  refetch: () => void
}

/**
 * Modern hook for managing settings with React Query.
 *
 * Features:
 * - Fetches settings using React Query
 * - Automatically syncs with react-hook-form
 * - Saves settings with optimistic updates
 * - Handles image uploads with detailed error messages
 *
 * @example
 * ```tsx
 * const { settings, isLoading, save, isSaving } = useSettings()
 * ```
 */
export const useSettings = (): UseSettingsReturn => {
  const { tenantId } = useContext(ActiveTenantContext)
  const queryClient = useQueryClient()
  const { reset } = useFormContext<Settings>()

  const getService = useTenantOptionalService(SettingsService)

  const settingsQuery = useQuery<Settings>({
    queryKey: queryKeys.settings(tenantId),
    queryFn: async () => {
      const service = await getService()
      return service.getSettings()
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  useEffect(() => {
    if (settingsQuery.data) {
      reset(settingsQuery.data)
    }
  }, [settingsQuery.data, reset])

  const saveMutation = useMutation({
    mutationFn: async (settings: Settings) => {
      const service = await getService()
      // Use PUT to save entire tenant configuration (shifts, rooms, employees, services)
      // PATCH only updates the settings JSONB column, not related entities
      return service.putSettings(settings)
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.settings(tenantId), data)

      queryClient.invalidateQueries({ queryKey: queryKeys.user() })
      invalidateTenantQueries(tenantId)
      reset(data)
      showToast({ severity: 'success', detail: 'Einstellungen erfolgreich gespeichert.' })
    },
    onError: (error) => {
      console.error('Settings save error:', error)
      showErrorToastUnlessPermissionDenied(error, { severity: 'error' })
    },
  })

  return {
    settings: settingsQuery.data,
    isLoading: settingsQuery.isLoading,
    isError: settingsQuery.isError,
    error: settingsQuery.error,
    isSaving: saveMutation.isPending,
    save: saveMutation.mutate,
    refetch: settingsQuery.refetch,
  }
}
